import React, { useEffect } from 'react';
import { DateTime as OmiliaDateTime } from '@omilia/chat-ui';
import { SDatetimeWrapper } from './styled';
import dayjs from 'dayjs';
import { getChatContainer } from 'utils/getChatContainer';

type StringDateInterval = {
  start: string;
  end: string;
};

type DateInterval = {
  start: Date;
  end: Date;
};

type Validation = {
  availableDates: StringDateInterval[];
  timeStep: number;
};

interface Props {
  withCalendar?: boolean;
  withTime?: boolean;
  submitButtonLabel?: string;
  validation?: Validation;
  semanticsKey?: string;
  locale?: string;
  onSubmit: (date: string) => void;
  triggerScroll: () => void;
}

const DateTime = ({
  onSubmit,
  withCalendar,
  withTime,
  validation = { timeStep: 1, availableDates: [] },
  submitButtonLabel,
  locale = 'en',
  triggerScroll,
}: Props) => {
  const [selectedDate, setDate] = React.useState(null);
  const [selectedTime, setTime] = React.useState('12 : 00 AM');

  const convertToDateIntervals = (
    intervals: StringDateInterval[],
  ): DateInterval[] | null => {
    return (
      intervals?.map(interval => ({
        start: dayjs(interval.start).toDate(),
        end: dayjs(interval.end).toDate(),
      })) || null
    );
  };

  const handleInputClick = () => {
    // It is needed to ensure that scroll to bottom is triggered after the scroll height changes
    setTimeout(() => {
      triggerScroll();
    }, 0);
  };

  const handleKeyDownFocus = (event: React.KeyboardEvent) => {
    // This is a workaround to focus the datepicker day element when using arrow keys
    // Issue is happening because webchat is placed inside a shadowDOM
    if (
      ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)
    ) {
      setTimeout(() => {
        const focusedEl = getChatContainer().querySelector(
          '.react-datepicker__day--keyboard-selected',
        ) as HTMLElement;
        if (focusedEl && focusedEl.tabIndex === 0) focusedEl.focus();
      }, 0);
    }
  };

  const formatTime = (time: string) => time?.replace(/\s*:\s*/g, ':');

  const formatDate = (date: Date) => dayjs(date)?.format('YYYY-MM-DD');

  const dateTime = withCalendar
    ? formatDate(selectedDate)
    : formatTime(selectedTime);

  return (
    <SDatetimeWrapper withTime={withTime}>
      {/* @ts-ignore */}
      <OmiliaDateTime
        inline
        locale={locale}
        onKeyDown={handleKeyDownFocus}
        onSubmit={() => onSubmit(dateTime)}
        onChange={date => setDate(date)}
        onTimeChange={time => setTime(time)}
        onInputClick={handleInputClick}
        selected={selectedDate}
        time={selectedTime}
        withCalendar={withCalendar}
        withTime={withTime}
        timeStep={validation.timeStep}
        includeDateIntervals={convertToDateIntervals(
          validation?.availableDates,
        )}
        submitButtonText={submitButtonLabel}
      />
    </SDatetimeWrapper>
  );
};

export default DateTime;
